import { Errors } from '@/constants/errors';
import { stripeService } from '@/services/stripe';
import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import { buildStripeGenericPaymentRequest } from '@/utils/stripe';
import type { CreatePaymentMethodData, StripeElements } from '@stripe/stripe-js';
import { logError } from '@/utils/logger';

// Single dispatch point for every Stripe-routed APM that renders a
// `<PaymentElement>` (which is all of them — see `StripeGenericPayForm`).
// Stripe derives the actual `type` from the rendered PaymentElement, so we
// only need to forward `elements` + `billing_details`. Adding a new APM is
// a zero-FE-change op as long as the backend lists it as STRIPE-provider.
//
// Lone exception: BLIK_CHECKOUT, which uses `StripeDirectPayForm` (no
// PaymentElement) and routes through `handleBlikCheckoutPayment` to preserve
// the literal `{ type: 'p24', p24: { bank: 'blik' } }` workaround.
export const handleGenericStripePayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: { elements?: StripeElements; email?: string; name?: string }
): Promise<unknown> => {
  try {
    const selectedPaymentMethod =
      checkout.paymentMethodSelected === PaymentMethodEnum.OTHER
        ? checkout.otherMethodSelected
        : checkout.paymentMethodSelected;
    const paymentMethodSettings = checkout.paymentMethodsSettings.find(
      (pm) => pm.paymentMethodName === selectedPaymentMethod
    );

    if (!paymentMethodSettings || !selectedPaymentMethod) {
      throw new Error(Errors.payment.paymentFailed);
    }
    if (!params.elements) {
      throw new Error('Elements are required for the Stripe generic flow');
    }

    const stripePaymentMethod = await stripeService.createGenericBankRequiredPaymentMethod({
      elements: params.elements,
      billing_details: {
        email: params.email ?? checkout.customer.email ?? '',
        name: params.name ?? '',
      },
    } as CreatePaymentMethodData & { elements: StripeElements });

    return await sendPaymentToServer(
      buildStripeGenericPaymentRequest(stripePaymentMethod, paymentMethodSettings, checkout.customer)
    );
  } catch (error) {
    logError(Errors.payment.paymentFailed, error);
  }
};
